import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, UserCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { getUserInfo, enableBackButton } from "../lib/telegram";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";

export const Settings = () => {
  const navigate = useNavigate();
  const { user, isNewUser } = useAuth();
  const telegramUser = getUserInfo();

  useEffect(() => {
    return enableBackButton(() => navigate("/"));
  }, [navigate]);

  const displayName =
    user?.name || telegramUser?.first_name || "User";
  const initial = displayName.charAt(0).toUpperCase();
  const username = telegramUser?.username;
  const roleName = user?.role || "user";

  return (
    <PageContainer>
      <GradientHeader size="md">
        <div className="flex items-center gap-3.5">
          <div className="w-14 h-14 rounded-full bg-ink flex items-center justify-center">
            <span className="text-2xl font-bold text-ground">{initial}</span>
          </div>
          <div>
            <p className="font-display text-xl font-semibold text-ink tracking-[-0.02em]">
              {displayName}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
              {roleName}
            </p>
          </div>
        </div>
      </GradientHeader>

      {/* Profile card */}
      <div className="px-4 mt-4">
        <SectionCard title="Profile">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted text-sm">Name</span>
              <span className="text-sm font-medium text-ink">
                {displayName}
              </span>
            </div>
            <div className="h-px bg-hairline" />
            <div className="flex justify-between items-center">
              <span className="text-muted text-sm">Role</span>
              <span className="stamp text-accent capitalize">{roleName}</span>
            </div>
            <div className="h-px bg-hairline" />
            <div className="flex justify-between items-center">
              <span className="text-muted text-sm">Telegram</span>
              <span className="text-sm font-medium text-ink">
                {username ? `@${username}` : telegramUser?.first_name || "Connected"}
              </span>
            </div>
          </div>
        </SectionCard>
      </div>

      {isNewUser && (
        <div className="px-4 mt-4">
          <div className="bg-surface border-l-[3px] border-accent rounded-md p-4">
            <p className="text-sm font-semibold text-ink mb-1">
              Complete your setup
            </p>
            <p className="text-xs text-muted leading-relaxed">
              Visit{" "}
              <a
                href="https://www.pufferfish.bot"
                className="font-semibold underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                pufferfish.bot
              </a>{" "}
              to link your Google account and unlock full features like crypto deposits and card management.
            </p>
          </div>
        </div>
      )}

      {/* Account */}
      <div className="px-4 mt-4">
        <SectionCard title="Account">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="w-full flex items-center justify-between py-1.5"
          >
            <span className="flex items-center gap-2.5">
              <UserCircle className="h-5 w-5 text-accent" />
              <span className="text-sm font-medium text-ink">
                Edit profile
              </span>
            </span>
            <ChevronRight className="h-4 w-4 text-muted/60" />
          </button>
        </SectionCard>
      </div>

      {/* About */}
      <div className="px-4 mt-4">
        <SectionCard title="About">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted text-sm">Version</span>
              <span className="font-mono text-sm font-medium text-ink">{__APP_VERSION__}</span>
            </div>
          </div>
        </SectionCard>
      </div>
    </PageContainer>
  );
};
